import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Flex, H1WithBack, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, Head1, OtherText, TitleText } from 'styling/Typography'
import { IOpsWorkerDay } from 'types/opsAnalytics.interface'
import { IOpsStatEntry, IOpsWorkerRow } from 'types/opsTimelines.interface'
import { IStatusPill } from 'types/ui/statusPill.interface'
import {
  liveTodayWorkerDay,
  OPS_TODAY_NOW_LABEL,
  OPS_WORKER_DAYS,
  opsDateLabelOf,
} from 'pages/Operations/Shared/opsAnalyticsData'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { BucketLegend, DayStrip } from './DayStrip'
import { LiveTodaySection } from './LiveTodaySection'
import { TimelineHistory } from './TimelineHistory'
import { StatCell, StatGrid, ToneValue } from './Timelines.styled'

const ACCESS_NOTE = 'Task-record review for supervisors — workers see their own timeline only.'

const WORKER_START_HOUR = 5

const NIGHT_START_HOUR = 17

const WORKER_WINDOW_HOURS = 14

const WORKER_SHIFTS: string[] = ['Day Shift', 'Night Shift']

const TODAY_WORKER_DAYS: IOpsWorkerDay[] = Array.from(
  new Set(OPS_WORKER_DAYS.map((day) => day.workerName)),
)
  .map((workerName) => liveTodayWorkerDay(workerName))
  .filter((day): day is IOpsWorkerDay => day != null)

const QUICK_DAYS: string[] = TODAY_WORKER_DAYS.length ? ['Today', 'Yesterday'] : ['Yesterday']

const PICKER_MIN_DATE = new Date(new Date().getFullYear(), 5, 2)

const gradeTone = (grade: number): IStatusPill['status'] =>
  grade >= 8 ? 'success' : grade >= 5 ? 'neutral' : 'danger'

const gradeLabel = (grade: number | null): string => (grade == null ? 'Not graded' : `${grade} / 10`)

const workerStats = (day: IOpsWorkerDay): IOpsStatEntry[] => [
  { label: 'Active hours', value: `${day.activeHours} h` },
  { label: 'Unassigned hours', value: `${day.unassignedHours} h` },
  { label: 'Tasks done', value: `${day.tasksDone}` },
  { label: 'Loads', value: `${day.loads}` },
  { label: 'Issues reported', value: `${day.issuesReported}` },
  { label: 'Grade', value: gradeLabel(day.grade) },
  { label: 'Reward points', value: `${day.rewardPoints}` },
  { label: 'Breaks', value: `${day.breaksMin} min` },
]

const workerColumns: IHonestTableColumn<IOpsWorkerRow>[] = [
  {
    id: 'worker',
    header: 'Worker',
    sticky: true,
    minWidth: '10rem',
    cell: (item) => <BodyText weight='600'>{item.workerName}</BodyText>,
  },
  {
    id: 'role',
    header: 'Role',
    minWidth: '11rem',
    cell: (item) => <BodyText grey>{item.role}</BodyText>,
  },
  {
    id: 'active',
    header: 'Active hrs',
    align: 'right',
    cell: (item) => <BodyText>{`${item.activeHours} h`}</BodyText>,
  },
  {
    id: 'unassigned',
    header: 'Unassigned hrs',
    align: 'right',
    cell: (item) => (
      <ToneValue $tone={item.unassignedHours > 1 ? 'warning' : undefined}>
        {`${item.unassignedHours} h`}
      </ToneValue>
    ),
  },
  {
    id: 'tasks',
    header: 'Tasks',
    align: 'right',
    cell: (item) => <BodyText>{item.tasksDone}</BodyText>,
  },
  {
    id: 'loads',
    header: 'Loads',
    align: 'right',
    cell: (item) => <BodyText>{item.loads}</BodyText>,
  },
  {
    id: 'issues',
    header: 'Issues',
    align: 'right',
    cell: (item) => <BodyText>{item.issuesReported}</BodyText>,
  },
  {
    id: 'grade',
    header: 'Grade',
    align: 'center',
    cell: (item) =>
      item.grade == null ? (
        <StatusPill status='neutral'>Not graded</StatusPill>
      ) : (
        <StatusPill status={gradeTone(item.grade)}>{item.grade}</StatusPill>
      ),
  },
  {
    id: 'points',
    header: 'Points',
    align: 'right',
    cell: (item) => <BodyText>{item.rewardPoints}</BodyText>,
  },
]

export const WorkerTimelines = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [selectedDay, setSelectedDay] = useState('Yesterday')
  const [pickedDate, setPickedDate] = useState<Date | null>(null)
  const [selectedShift, setSelectedShift] = useState('Day Shift')
  const selectQuickDay = (label: string) => {
    setSelectedDay(label)
    setPickedDate(null)
  }
  const handlePickDate = (date: Date | null) => {
    setPickedDate(date)
    if (date) setSelectedDay(opsDateLabelOf(date))
  }

  if (!USE_OPS_MOCKS) {
    return (
      <>
        <NavBar />
        <PageWrapper>
          <Head1>Worker timelines</Head1>
          <NoData text='Timelines are computed at shift close once connected.' />
        </PageWrapper>
      </>
    )
  }

  if (id) {
    const days = OPS_WORKER_DAYS.filter((w) => w.workerId === Number(id))
    const day = days.find((d) => d.dateLabel === 'Yesterday') ?? days[0] ?? null
    const prior = days.filter((d) => d !== day)

    if (!day) {
      return (
        <>
          <NavBar />
          <PageWrapper>
            <Flex center gap='0.75rem'>
              <H1WithBack title='Worker timeline' arrow onClick={() => navigate('/operations/analytics/workers')} />
              <DemoBadge />
            </Flex>
            <NoData text='No timeline recorded for this worker.' />
          </PageWrapper>
        </>
      )
    }

    const shiftName = day.shiftName ?? 'Day Shift'
    const detailStartHour = shiftName === 'Night Shift' ? NIGHT_START_HOUR : WORKER_START_HOUR

    return (
      <>
        <NavBar />
        <PageWrapper>
          <Flex center gap='0.75rem'>
            <H1WithBack title={day.workerName} arrow onClick={() => navigate('/operations/analytics/workers')} />
            <DemoBadge />
          </Flex>
          <Flex column gap='0.25rem' mb='16'>
            <OtherText grey>{`${day.role} · ${shiftName} · login ${day.loginAt} · logout ${day.logoutAt}`}</OtherText>
            <OtherText grey>{ACCESS_NOTE}</OtherText>
          </Flex>
          <Flex column gap='1rem' width='100%'>
            <LiveTodaySection workerDay={liveTodayWorkerDay(day.workerName)} />
            <Container>
              <ContainerHeader>
                <TitleText>{day.dateLabel}</TitleText>
              </ContainerHeader>
              <ContainerContent gap='0.75rem'>
                <DayStrip
                  segments={day.segments}
                  dateLabel={`${day.dateLabel} · ${day.loginAt} → ${day.logoutAt}`}
                  variant='worker'
                  startHour={detailStartHour}
                  windowHours={WORKER_WINDOW_HOURS}
                />
                <BucketLegend />
              </ContainerContent>
            </Container>
            <Container>
              <ContainerHeader>
                <TitleText>Totals</TitleText>
              </ContainerHeader>
              <ContainerContent>
                <StatGrid>
                  {workerStats(day).map((stat) => (
                    <StatCell key={stat.label}>
                      <OtherText grey>{stat.label}</OtherText>
                      <BodyText weight='600'>{stat.value}</BodyText>
                    </StatCell>
                  ))}
                </StatGrid>
              </ContainerContent>
            </Container>
            {prior.length > 0 && (
              <TimelineHistory
                variant='worker'
                workerDays={prior}
                startHour={detailStartHour}
                windowHours={WORKER_WINDOW_HOURS}
              />
            )}
          </Flex>
        </PageWrapper>
      </>
    )
  }

  const dayRows =
    selectedDay === 'Today'
      ? TODAY_WORKER_DAYS
      : OPS_WORKER_DAYS.filter((w) => w.dateLabel === selectedDay)
  const rows: IOpsWorkerRow[] = dayRows
    .filter((w) => (w.shiftName ?? 'Day Shift') === selectedShift)
    .map((w) => ({
      ...w,
      id: w.workerId,
    }))

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex center gap='0.75rem'>
          <Head1>Worker timelines</Head1>
          <DemoBadge />
        </Flex>
        <Flex column gap='0.25rem' mb='16'>
          <OtherText grey>
            {selectedDay === 'Today'
              ? `Today · ${selectedShift} · 06:00 → ${OPS_TODAY_NOW_LABEL} — in progress`
              : `${selectedDay} · ${selectedShift}`}
          </OtherText>
          <OtherText grey>{ACCESS_NOTE}</OtherText>
        </Flex>
        <Flex mb='16' center gap='0.75rem' wrap>
          <TabsContainer>
            {QUICK_DAYS.map((label) => (
              <Tabs key={label} active={selectedDay === label} onClick={() => selectQuickDay(label)}>
                {label}
              </Tabs>
            ))}
          </TabsContainer>
          <CalendarInput
            name='timelineDay'
            placeholder='Jump to date'
            width='12rem'
            nomarg
            defaultValue={pickedDate}
            minDate={PICKER_MIN_DATE}
            maxDate={new Date()}
            onChange={handlePickDate}
          />
          <TabsContainer>
            {WORKER_SHIFTS.map((shift) => (
              <Tabs key={shift} active={selectedShift === shift} onClick={() => setSelectedShift(shift)}>
                {shift}
              </Tabs>
            ))}
          </TabsContainer>
        </Flex>
        <HonestTable
          columns={workerColumns}
          data={rows}
          keyField='id'
          emptyMessage='No records for this day.'
          onClick={(item) => navigate(`/operations/analytics/workers/${item.workerId}`)}
        />
      </PageWrapper>
    </>
  )
}
